import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { TOOLTIP_EDGE_MARGIN, TOOLTIP_GAP } from "../constants";

interface Position {
  left: number;
  top: number;
  arrow: number;
  below: boolean;
}

/** A small "?" that explains a calculated value. Opens on hover, keyboard focus or tap. */
export function HelpTip({ label, children }: { label: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<Position | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLSpanElement>(null);
  const id = useId();

  // Place the tip against the viewport (not its parent) so it can't run off either edge.
  const place = useCallback(() => {
    if (!btnRef.current || !popRef.current) return;
    const b = btnRef.current.getBoundingClientRect();
    const p = popRef.current.getBoundingClientRect();
    const centre = b.left + b.width / 2;
    const left = Math.min(
      Math.max(centre - p.width / 2, TOOLTIP_EDGE_MARGIN),
      Math.max(TOOLTIP_EDGE_MARGIN, window.innerWidth - p.width - TOOLTIP_EDGE_MARGIN),
    );
    const above = b.top - p.height - TOOLTIP_GAP >= TOOLTIP_EDGE_MARGIN;
    setPos({
      left,
      top: above ? b.top - p.height - TOOLTIP_GAP : b.bottom + TOOLTIP_GAP,
      arrow: centre - left,
      below: !above,
    });
  }, []);

  // Measuring and then storing the result is what a layout effect is for: the tip's
  // position can only be known once it has been laid out. This runs before paint, so
  // the tip is never visible in the wrong place.
  useLayoutEffect(() => {
    if (open) place();
    // eslint-disable-next-line react-hooks/set-state-in-effect -- see above
    else setPos(null);
  }, [open, place]);

  // The tip is fixed to the viewport, so follow the button while the page scrolls or resizes.
  useEffect(() => {
    if (!open) return;
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, place]);

  return (
    <span className="help" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        ref={btnRef}
        type="button"
        className="help-btn"
        aria-label={label}
        aria-describedby={open ? id : undefined}
        // Focus covers both keyboard and tap; a click handler as well would
        // close the tip again on the same tap that opened it.
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
      >
        <span aria-hidden="true">?</span>
      </button>
      {open && (
        <span
          ref={popRef}
          role="tooltip"
          id={id}
          className={"help-pop" + (pos?.below ? " below" : "")}
          style={
            {
              left: pos?.left ?? 0,
              top: pos?.top ?? 0,
              visibility: pos ? "visible" : "hidden",
              "--arrow-x": `${pos?.arrow ?? 0}px`,
            } as CSSProperties
          }
        >
          {children}
        </span>
      )}
    </span>
  );
}
